import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box } from "@mui/material";
import ChatPromptInput from "src/reusable/Chat/PromptInput/ChatPromptInput";
import { FileUploadState } from "src/reusable/types/fileUploadWithProgress";
import { FileWithPreview } from "src/reusable/Chat/types";
import { NucToast } from "src/reusable/components/Toast";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { ImageContent } from "src/views/assistant/types";
import { type TokenUsageData } from "src/reusable/Chat/TokenUsageGauge";
import { useProposalGenerationStore } from "../store/ProposalGenerationStore";
import {
  type ProposalGenerationDialogueData,
  useUpdateProposalGenerationDialogueData,
} from "../services/ProposalGenerationServices";
import {
  useProcessingStatusBySdrIds,
  type ProcessingStatus,
} from "src/views/products/workflow/services/ArtifactEnrichmentService";
import { cleanArtifactFilename } from "src/utils/artifactUtils";
import { useIsPermissionDenied } from "src/helpers/Auth";

type ProposalGenerationChatInputProps = {
  chatStreamError: string | null;
  isChatConnectionError: boolean | null;
  isChatConnected: boolean;
  isChatLoading: boolean;
  /** True while the model is actively generating in the current turn. */
  isTurnActive?: boolean;
  InputButtons?: React.FC<any>;
  InputBar?: React.FC<any>;
  isSplashScreen?: boolean;
  terminateConnection: () => void;
  sendUserMessage: (text: string, images?: ImageContent[]) => Promise<boolean>;
  mergedFiles?: FileWithPreview[];
  tokenUsage?: TokenUsageData | null;
  proposalGenDialogueData?: ProposalGenerationDialogueData | null;
  refetchProposalData?: () => void | Promise<unknown>;
};

const ProposalGenerationChatInput = ({
  chatStreamError,
  isChatConnectionError,
  isChatLoading,
  isTurnActive = false,
  InputButtons,
  InputBar,
  isSplashScreen = false,
  terminateConnection,
  sendUserMessage,
  tokenUsage,
  proposalGenDialogueData,
  refetchProposalData,
}: ProposalGenerationChatInputProps) => {
  const [userInput, setUserInput] = useState<string>("");
  const isUpdateDialogueDataDenied = useIsPermissionDenied("artifact:write");

  // track attached files from the chunked upload system
  const { uploadedFiles, addUploadedFile, removeUploadedFile } = useChatStore();

  // Proposal generation stores and mutation for syncing mid-chat file uploads
  const { addInputFile, removeInputFile } = useProposalGenerationStore();
  const { mutateAsync: updateProposalData } =
    useUpdateProposalGenerationDialogueData();
  const dialogueId = useProposalGenerationStore((s) => s.dialogueId);

  // Maintain locally authoritative dialogue config to avoid race conditions from stale props
  // Includes artifact IDs and other fields needed for mutations
  // Initialized from proposalGenDialogueData and kept in sync with successful mutations
  const confirmedDialogueConfigRef = useRef<{
    input_artifact_ids: number[];
    dataset_ids: number[];
    template_id: number | null;
    report_sdr_id: number | null;
  }>({
    input_artifact_ids: [],
    dataset_ids: [],
    template_id: null,
    report_sdr_id: null,
  });

  // Serialize mutations to prevent concurrent updates from overwriting each other
  const isUpdatingRef = useRef(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize and sync local dialogue config from proposalGenDialogueData
  useEffect(() => {
    if (proposalGenDialogueData) {
      confirmedDialogueConfigRef.current = {
        input_artifact_ids: proposalGenDialogueData.input_artifact_ids ?? [],
        dataset_ids: proposalGenDialogueData.dataset_ids ?? [],
        template_id: proposalGenDialogueData.template_id ?? null,
        report_sdr_id: proposalGenDialogueData.report_sdr_id ?? null,
      };
    }
  }, [
    proposalGenDialogueData?.input_artifact_ids,
    proposalGenDialogueData?.dataset_ids,
    proposalGenDialogueData?.template_id,
    proposalGenDialogueData?.report_sdr_id,
  ]);

  // ---- Mid-chat upload tracking ----
  // Track files that were uploaded mid-chat (not from splash page / dialogue history).
  // We store their info so we can poll processing status and sync when done.
  type PendingUpload = {
    artifactId: number;
    sdrId: number;
    datasetId: number;
    fileName: string;
    fileSize: number;
  };
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);

  // Detect newly uploaded files from the chat input and start tracking them
  useEffect(() => {
    for (const file of uploadedFiles) {
      if (
        file.uploadStatus !== "uploaded" ||
        file.isFromDialogueHistory ||
        file.isFromProject
      ) {
        continue;
      }

      const artifactId =
        typeof file.artifactId === "number" ? file.artifactId : undefined;
      const sdrId = file.sourceDataRecordId;
      const datasetId = file.datasetId;

      // Need artifact + sdr + dataset to track
      if (!artifactId || !sdrId || !datasetId) continue;

      // Skip if already pending or already synced to server
      const isAlreadyPending = pendingUploads.some(
        (p) => p.artifactId === artifactId,
      );
      const isAlreadySynced =
        confirmedDialogueConfigRef.current.input_artifact_ids.includes(
          artifactId,
        );
      if (isAlreadyPending || isAlreadySynced) continue;

      setPendingUploads((prev) => {
        if (prev.some((p) => p.artifactId === artifactId)) return prev;
        return [
          ...prev,
          {
            artifactId,
            sdrId,
            datasetId,
            fileName: file.file.name,
            fileSize: file.file.size,
          },
        ];
      });
    }
  }, [uploadedFiles]);

  const pendingSdrIds = useMemo(
    () => pendingUploads.map((p) => p.sdrId),
    [pendingUploads],
  );

  // Poll processing status for mid-chat uploaded files
  const { data: processingStatuses = [] } = useProcessingStatusBySdrIds(
    pendingSdrIds,
    pendingSdrIds.length > 0,
  );

  // When processing completes for a pending file, add to input_artifacts + inputFiles
  useEffect(() => {
    if (processingStatuses.length === 0 || pendingUploads.length === 0) return;

    // Guard: only sync when dialogue data is fully loaded to avoid wiping backend state
    if (!proposalGenDialogueData) return;

    // Skip if an update is already in flight to avoid race conditions
    if (isUpdatingRef.current) {
      return;
    }

    // First pass: collect all newly completed artifacts and track failures
    const newlyCompleted: PendingUpload[] = [];
    const failedUploads: PendingUpload[] = [];
    for (const pending of pendingUploads) {
      // Skip if already synced to server (shouldn't happen since we check on add)
      if (
        confirmedDialogueConfigRef.current.input_artifact_ids.includes(
          pending.artifactId,
        )
      )
        continue;

      // Find matching processing status by SDR ID
      const status = processingStatuses.find(
        (s: ProcessingStatus) => s.source_data_record_id === pending.sdrId,
      );

      // Not yet available or still processing
      if (!status || status.is_processing) continue;

      // Processing failed
      if (status.error_message) {
        console.error(
          `[ProposalGenerationChatInput] Processing failed for "${pending.fileName}": ${status.error_message}`,
        );
        failedUploads.push(pending);
        // Surface user-visible error
        NucToast({
          message: `Processing failed for "${pending.fileName}". Please try uploading again.`,
          type: "error",
          hideProgressBar: false,
        });
        continue;
      }

      // Processing complete — sync to backend
      newlyCompleted.push(pending);
    }

    // Remove failed uploads from pending list to stop polling for them
    if (failedUploads.length > 0) {
      setPendingUploads((prev) =>
        prev.filter(
          (p) => !failedUploads.some((f) => f.artifactId === p.artifactId),
        ),
      );
    }

    if (newlyCompleted.length === 0) return;

    // Derive updates from locally authoritative confirmed config, not stale props
    const existingIds = confirmedDialogueConfigRef.current.input_artifact_ids;
    const newIds = newlyCompleted
      .map((p) => p.artifactId)
      .filter((id) => !existingIds.includes(id));
    const updatedIds = [...existingIds, ...newIds];

    // Mark as updating to serialize concurrent mutations
    isUpdatingRef.current = true;

    updateProposalData({
      dialogueId: dialogueId!,
      payload: {
        input_artifact_ids: updatedIds,
        dataset_ids: proposalGenDialogueData.dataset_ids,
        template_id: proposalGenDialogueData.template_id,
        report_sdr_id: proposalGenDialogueData.report_sdr_id,
      },
    })
      .then(() => {
        if (!isMountedRef.current) return;

        // Update local confirmed config with current backend state
        confirmedDialogueConfigRef.current = {
          ...confirmedDialogueConfigRef.current,
          input_artifact_ids: updatedIds,
        };

        // Add all completed uploads to sidebar
        for (const pending of newlyCompleted) {
          addInputFile({
            name: cleanArtifactFilename(pending.fileName),
            size: pending.fileSize,
            id: pending.artifactId,
            datasetId: pending.datasetId,
            sdrId: pending.sdrId,
            uploadProgress: 100,
            processingProgress: 100,
            uploadStatus: "uploaded",
            processingStatus: "completed",
          });

          // Update search context
          const currentSdrIds = useChatStore.getState().sourceDataRecordIds;
          if (!currentSdrIds.includes(pending.sdrId)) {
            useChatStore
              .getState()
              .setSourceDataRecordIds([...currentSdrIds, pending.sdrId]);
          }
        }

        refetchProposalData?.();

        // Remove all newly completed artifacts from pending list
        setPendingUploads((prev) =>
          prev.filter(
            (p) => !newlyCompleted.some((c) => c.artifactId === p.artifactId),
          ),
        );
      })
      .catch((err) => {
        console.error(
          `[ProposalGenerationChatInput] Failed to sync newly completed artifacts:`,
          err,
        );
        // Artifacts remain in pendingUploads for retry on next polling cycle
      })
      .finally(() => {
        // Mark update as complete to allow next batch
        isUpdatingRef.current = false;
      });
  }, [
    processingStatuses,
    pendingUploads,
    dialogueId,
    proposalGenDialogueData?.dataset_ids,
    proposalGenDialogueData?.template_id,
    proposalGenDialogueData?.report_sdr_id,
    updateProposalData,
    addInputFile,
    refetchProposalData,
  ]);

  // Handle file removal — remove from inputFiles and update input_artifacts
  const handleRemoveFile = useCallback(
    (file: FileUploadState) => {
      const artifactId =
        typeof file.artifactId === "number" ? file.artifactId : undefined;
      const sdrId = file.sourceDataRecordId;
      // Both required to sync the removal to the backend (see the matching
      // `if` below); computed once so the permission guard and the sync
      // block gate on the same condition instead of repeating it.
      const willSyncToBackend = Boolean(artifactId && dialogueId);

      // Check permission before touching any local state — otherwise the file
      // disappears from the UI (sidebar, search context) while the backend
      // update below is skipped, and it silently reappears on next load.
      if (willSyncToBackend && isUpdateDialogueDataDenied) {
        NucToast({
          message: "User doesn't have permissions artifact:write",
          type: "error",
          hideProgressBar: false,
        });
        return;
      }

      // Remove from pending uploads (if still processing)
      if (artifactId) {
        setPendingUploads((prev) =>
          prev.filter((p) => p.artifactId !== artifactId),
        );
      }

      // Remove from inputFiles sidebar
      if (artifactId) {
        removeInputFile(artifactId);
      }

      // Remove from search context
      if (sdrId) {
        const currentSdrIds = useChatStore.getState().sourceDataRecordIds;
        useChatStore
          .getState()
          .setSourceDataRecordIds(currentSdrIds.filter((id) => id !== sdrId));
      }

      // Update backend input_artifacts (remove this artifact)
      if (willSyncToBackend) {
        const prevConfig = { ...confirmedDialogueConfigRef.current };
        const updatedArtifactIds = prevConfig.input_artifact_ids.filter(
          (id) => id !== artifactId,
        );

        updateProposalData({
          dialogueId: dialogueId!,
          payload: {
            input_artifact_ids: updatedArtifactIds,
            dataset_ids: prevConfig.dataset_ids,
            template_id: prevConfig.template_id,
            report_sdr_id: prevConfig.report_sdr_id,
          },
        })
          .then(() => {
            // Update local config only after successful mutation
            confirmedDialogueConfigRef.current = {
              ...confirmedDialogueConfigRef.current,
              input_artifact_ids: updatedArtifactIds,
            };
            refetchProposalData?.();
          })
          .catch((err) => {
            // Ref was never updated — no rollback needed
            console.error(
              `[ProposalGenerationChatInput] Failed to remove artifact ${artifactId}:`,
              err,
            );
          });
      }
    },
    [
      dialogueId,
      updateProposalData,
      removeInputFile,
      refetchProposalData,
      isUpdateDialogueDataDenied,
    ],
  );

  // handle file changes from the chunked upload system
  const handleFilesChange = (files: FileUploadState[]) => {
    // clear existing user-uploaded files (but NOT private dataset docs or project attachments)
    uploadedFiles.forEach((file: any) => {
      if (!file.isFromDialogueHistory && !file.isFromProject) {
        removeUploadedFile(file.id);
      }
    });

    // add new files
    files.forEach((file) => addUploadedFile(file));
  };

  const handleFormSubmit = async () => {
    // Block submission only when model is actively loading or generating
    if (isChatLoading || isTurnActive) {
      return;
    }

    // Don't allow empty messages unless there are files attached
    if (userInput.trim() === "" && uploadedFiles.length === 0) {
      NucToast({
        message: "Please enter a message or attach files",
        type: "error",
        hideProgressBar: false,
      });
      return;
    }

    // Send the message with files (allow sending even if files are processing)
    const sentSuccessfully = await sendUserMessage(userInput); // TODO: add images support
    if (sentSuccessfully) {
      setUserInput("");
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(event.target.value);
  };

  const handleStopChat = () => {
    terminateConnection();
  };

  useEffect(() => {
    if (chatStreamError || isChatConnectionError) {
      terminateConnection();
    }
  }, [chatStreamError, terminateConnection, isChatConnectionError]);

  return (
    <Box data-testid="propgen-chat-input" sx={{ display: "contents" }}>
      <ChatPromptInput
        onSubmit={handleFormSubmit}
        isInputting={!isChatLoading && !isTurnActive && userInput !== ""}
        isChatLoading={isChatLoading}
        isGenerating={isTurnActive}
        onStopChat={handleStopChat}
        value={userInput}
        onChange={handleChange}
        onFilesChange={handleFilesChange}
        onRemoveFile={handleRemoveFile}
        InputBar={InputBar}
        InputButtons={InputButtons}
        isSplashScreen={isSplashScreen}
        placeholder="What can I help you with?"
        product="proposal-generation"
        tokenUsage={tokenUsage}
        stopBtnTestId="propgen-btn-stop"
      />
    </Box>
  );
};

export default ProposalGenerationChatInput;
